import React, { useState } from 'react';
import { useProCalculator } from '../hooks/useProCalculator';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../lib/supabase';
import LoginGateModal from './LoginGateModal';
import { AlertTriangle } from 'lucide-react';
import '../styles/ProCalculatorFooter.css';

const ProCalculatorFooter = ({ onNext, nextLabel, nextDisabled = false, resultsModeOnly = false, onBack, backLabel }) => {
  const { 
    reference,
    useDefaultReference,
    cookieRows,
    setRows,
    dimensionsRows,
    colorRows,
    colorNames,
    orderRows,
    useSets,
    isMetric,
    isMetricWeight,
    loadedPlanId,
    setLoadedPlanId,
    loadedPlanName,
    setLoadedPlanName,
    setReference,
    setUseDefaultReference,
    setCookieRows,
    setSetRows,
    setDimensionsRows,
    setColorRows,
    setColorNames,
    setOrderRows,
    setUseSets,
    setActiveSection,
    detailAmounts,
    setDetailAmounts,
    hasUnsavedChanges,
    markSaved,
  } = useProCalculator();
  
  const { user } = useAuth();
  const [showSaveAsModal, setShowSaveAsModal] = useState(false);
  const [showLoginGate, setShowLoginGate] = useState(false);
  const [showConfirmClear, setShowConfirmClear] = useState(false);
  const [planName, setPlanName] = useState('');
  const [planDescription, setPlanDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Get current calculator state
  const getCalculatorState = () => ({
    reference,
    useDefaultReference,
    cookieRows,
    setRows,
    dimensionsRows,
    colorRows,
    colorNames,
    orderRows,
    useSets,
    isMetric,
    isMetricWeight,
    detailAmounts,
  });

  // Clear all data
  const handleClear = () => {
    setShowConfirmClear(true);
  };

  const doClear = () => {
    setShowConfirmClear(false);
    // Clear loaded plan tracking
    setLoadedPlanId(null);
    setLoadedPlanName(null);
    markSaved();
    
    // Build a fresh cookie ID so all linked rows stay in sync
    const freshCookieId = `cookie_${Date.now()}`;

    // Reset all calculator data using correct field shapes
    setReference({ width: '', length: '', weightNoIcing: '', weight: '' });
    setUseDefaultReference(true);
    setCookieRows([{ id: freshCookieId, cookieName: '', type: 'rectangle', width: '', length: '', diameter: '', unit: 'in', notes: '', isMetric: false }]);
    setSetRows([{ id: `set_${Date.now()}`, setName: 'Set 1', cookieId: '', count: 1 }]);
    setDimensionsRows([{ id: `dim_${Date.now()}`, cookieId: freshCookieId, shapeType: 'rectangle', width: '', length: '', diameter: '', area: 0, icingNeeded: 0 }]);
    setColorRows([{ id: `col_${Date.now()}`, cookieId: freshCookieId, percentages: [100] }]);
    setColorNames(['White']);
    setOrderRows([{ id: `ord_${Date.now()}`, cookieId: freshCookieId, count: 0 }]);
    setDetailAmounts({ detail: [0], floral: [0], other: [0] });
    setUseSets(false);
    
    // Navigate to first section after a brief delay to allow state to propagate
    setTimeout(() => {
      setActiveSection('reference');
    }, 100);
  };

  // Save current plan (update if loaded, otherwise prompt for name)
  const handleSave = async () => {
    if (!user) {
      setShowLoginGate(true);
      return;
    }

    if (loadedPlanId) {
      // Update existing plan — no description field on this quick-save path,
      // so don't touch the stored description (see savePlan below).
      await savePlan(loadedPlanId, loadedPlanName);
    } else {
      // No plan loaded, show Save As modal
      handleSaveAs();
    }
  };

  // Save As - always prompt for new name
  const handleSaveAs = () => {
    if (!user) {
      setShowLoginGate(true);
      return;
    }
    setPlanName(loadedPlanName || '');
    setPlanDescription('');
    setShowSaveAsModal(true);
  };

  // Save plan to Supabase.
  // `description` is optional — omit it (as the quick "Save" button does) to
  // leave the plan's stored description untouched. Only "Save As" passes one
  // in, since that's the only modal with a description field.
  const savePlan = async (planId, name, description) => {
    setSaving(true);
    try {
      const calculatorState = getCalculatorState();
      const planData = {
        name: name.trim(),
        data: JSON.stringify(calculatorState),
        updated_at: new Date().toISOString()
      };
      if (description !== undefined) {
        planData.description = description.trim();
      }

      if (planId) {
        // Update existing plan
        const { data, error } = await supabase
          .from('projects')
          .update(planData)
          .eq('id', planId)
          .eq('user_id', user.id)
          .select();

        if (error) throw error;

        if (data && data.length > 0) {
          markSaved();
          showToast(`Plan "${name}" updated!`);
        } else {
          // Plan no longer exists (e.g. deleted in another session) —
          // fall back to inserting as a new plan so the user doesn't lose work.
          planData.user_id = user.id;
          planData.created_at = new Date().toISOString();
          const { data: inserted, error: insertError } = await supabase
            .from('projects')
            .insert([planData])
            .select();
          if (insertError) throw insertError;
          if (inserted && inserted.length > 0) {
            setLoadedPlanId(inserted[0].id);
            setLoadedPlanName(inserted[0].name);
            markSaved();
            showToast(`Original plan was missing — saved as new plan "${name}".`);
          }
        }
      } else {
        // Insert new plan
        planData.user_id = user.id;
        planData.created_at = new Date().toISOString();
        
        const { data, error } = await supabase
          .from('projects')
          .insert([planData])
          .select();

        if (error) throw error;
        
        if (data && data.length > 0) {
          setLoadedPlanId(data[0].id);
          setLoadedPlanName(data[0].name);
          markSaved();
          showToast(`Plan "${name}" saved!`);
        }
      }
    } catch (error) {
      showToast('Error saving plan: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Handle Save As form submission
  const handleSaveAsSubmit = async (e) => {
    e.preventDefault();
    if (!planName.trim()) {
      showToast('Please enter a plan name.', 'error');
      return;
    }
    
    await savePlan(null, planName, planDescription);
    setShowSaveAsModal(false);
    setPlanName('');
    setPlanDescription('');
  };

  const saveButtons = (
    <div className="footer-left-buttons">
      <button
        className={`btn-primary${loadedPlanId && !hasUnsavedChanges ? ' btn-saved' : ''}`}
        onClick={handleSave}
        disabled={saving || (loadedPlanId && !hasUnsavedChanges)}
        title={loadedPlanId && !hasUnsavedChanges ? 'No changes since last save' : 'Save current plan'}
      >
        <svg className="button-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
          <polyline points="17 21 17 13 7 13 7 21"></polyline>
          <polyline points="7 3 7 8 15 8"></polyline>
        </svg>
        <span className="button-label">Save</span>
      </button>
      
      {loadedPlanId && (
        <button
          className="btn-primary"
          onClick={handleSaveAs}
          disabled={saving}
          title="Duplicate this plan"
        >
          <svg className="button-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
          <span className="button-label">Duplicate</span>
        </button>
      )}
      
      <button
        className="btn-delete"
        onClick={handleClear}
        title="Clear all data"
      >
        <svg className="button-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="3 6 5 6 21 6"></polyline>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
        </svg>
        <span className="button-label">Clear</span>
      </button>
    </div>
  );

  return (
    <>
      {toast && (
        <div className={`pcf-toast pcf-toast-${toast.type}`}>{toast.message}</div>
      )}

      {resultsModeOnly ? (
        <div className="pro-calculator-footer">
          {saveButtons}
          {onBack && (
            <button className="btn-primary" onClick={onBack}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5"></path>
                <path d="M12 19l-7-7 7-7"></path>
              </svg>
              <span className="button-label">{backLabel}</span>
            </button>
          )}
        </div>
      ) : (
        <div className="pro-calculator-footer">
          {saveButtons}
          <div className="footer-nav-buttons">
            {onBack && (
              <button
                className="btn-primary"
                onClick={onBack}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5"></path>
                  <path d="M12 19l-7-7 7-7"></path>
                </svg>
                <span className="button-label">{backLabel}</span>
              </button>
            )}
            {onNext && (
              <button
                className="btn-primary"
                onClick={onNext}
                disabled={nextDisabled}
              >
                <span className="button-label">{nextLabel}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"></path>
                  <path d="M12 5l7 7-7 7"></path>
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Confirm Clear Modal */}
      {showConfirmClear && (
        <div className="modal-overlay" onClick={() => setShowConfirmClear(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Clear Everything?</h2>
            {hasUnsavedChanges && loadedPlanId ? (
              <p>
                <AlertTriangle size={18} className="pcf-inline-icon" />
                <strong>You have unsaved changes to "{loadedPlanName}".</strong> Clearing will permanently discard them.
              </p>
            ) : (
              <p>This will clear all your current entries. Any unsaved work will be lost.</p>
            )}
            <div className="form-actions">
              <button
                type="button"
                onClick={() => setShowConfirmClear(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doClear}
                className="btn-delete"
              >
                Yes, Clear It
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save As Modal */}
      {showSaveAsModal && (
        <div className="modal-overlay" onClick={() => setShowSaveAsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Save Plan As</h2>
            <form onSubmit={handleSaveAsSubmit}>
              <div className="form-group">
                <label htmlFor="savePlanName">Plan Name</label>
                <input
                  id="savePlanName"
                  type="text"
                  value={planName}
                  onChange={(e) => setPlanName(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="savePlanDescription">Description (optional)</label>
                <textarea
                  id="savePlanDescription"
                  value={planDescription}
                  onChange={(e) => setPlanDescription(e.target.value)}
                  rows="3"
                />
              </div>
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => setShowSaveAsModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={saving}
                  className="btn-primary"
                >
                  {saving ? 'Saving...' : 'Save Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <LoginGateModal
        open={showLoginGate}
        onClose={() => setShowLoginGate(false)}
        action="save your icing plan"
        subtitle="Create a free account to keep your icing plan saved and ready when you return. It only takes a few seconds, everything you've entered will stay right here, and you'll get the AI tools for free too."
      />
    </>
  );
};

export default ProCalculatorFooter;
